import type RulebookAttachTarget from "@next-platform/client-common-core/rulebook/attach/RulebookAttachTarget";
import { Chip, ChipSize, ChipVariant, Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useCallback, useMemo } from "react";

import Trans from "@/components/Trans/Trans";
import { AvatarPreset } from "@/types";
import { getAvatarUrl } from "@/utils/getAvatarUrl";

type RulebookAttachTargetCardProps = {
  target: RulebookAttachTarget;
  rulebookId: string;
  isSelected: boolean;
  disabled: boolean;
  onSelect: (id: string) => void;
};

const RulebookAttachTargetCard = ({ target, rulebookId, isSelected, disabled, onSelect }: RulebookAttachTargetCardProps) => {
  const isReplacing = isSelected && target.attachedRulebookId !== null && target.attachedRulebookId !== rulebookId;
  const avatarUrl = target.avatarUrl ? (getAvatarUrl(target.avatarUrl, { preset: AvatarPreset.AVATAR_64X64 }) ?? "") : "";
  const handleClick = useCallback(() => onSelect(target.id), [onSelect, target.id]);
  const replacesValues = useMemo(() => ({ name: target.attachedRulebookName ?? "" }), [target]);

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      data-testid="RulebookAttachTargetCard"
      onClick={handleClick}
      className={`flex w-full items-center gap-sm rounded-md p-sm text-left transition-colors ${
        isSelected ? "bg-gray-5" : "bg-transparent hover:bg-gray-4"
      }`}
      disabled={disabled}
    >
      {avatarUrl ? (
        <img src={avatarUrl} alt={target.name} className="h-[64px] w-[64px] flex-none rounded-md object-cover" />
      ) : (
        <div className="flex h-[64px] w-[64px] flex-none items-center justify-center rounded-md bg-gray-6 text-gray-10">
          <Icon icon={IconVariants.BOT} size={IconSize.MEDIUM} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <Text variant={TextVariants.LABEL_MEDIUM_BOLD} className="min-w-0 truncate text-gray-12">
            {target.name}
          </Text>
          {target.attachedRulebookId ? (
            <Chip size={ChipSize.XSMALL} variant={ChipVariant.ACTIVE} iconStart={IconVariants.BLOCKS}>
              <Trans i18nKey="rulebook:attach.badge" />
            </Chip>
          ) : null}
        </div>
        {isReplacing ? (
          <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-yellow-10" data-testid="RulebookAttachTargetCard-Replaces">
            {/* Owner payloads don't always carry the attached Rulebook's name — fall back to generic copy, never the raw id. */}
            {target.attachedRulebookName ? (
              <Trans i18nKey="rulebook:attach.replaces" values={replacesValues} />
            ) : (
              <Trans i18nKey="rulebook:attach.replacesCurrent" />
            )}
          </Text>
        ) : null}
      </div>
      <input
        type="radio"
        name="rulebook-attach-target"
        checked={isSelected}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        data-testid="RulebookAttachTargetCard-Radio"
        className="h-4 w-4 flex-none accent-blue-10"
        disabled={disabled}
      />
    </button>
  );
};

export default RulebookAttachTargetCard;
